import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import bentoErrorPage from '../public/bento_error_bg.png';
import { bentoTeal3, pureWhite } from './colors';
interface ErrorPageProps {
  resetError: () => void;
  error?: Error;
}

const Overlay = styled(Box)({
  color: pureWhite,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'grid',
  placeItems: 'center',
});

const Background = styled('div')(({ theme }) => ({
  height: '100vh',
  width: '100vw',
  backgroundImage: `url(${bentoErrorPage})`,
  borderTop: `${theme.spacing(2)} solid ${bentoTeal3}`,
  borderBottom: `${theme.spacing(2)} solid ${bentoTeal3}`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

function ErrorPage({ resetError, error }: ErrorPageProps) {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

  // detect chunk loading error (new version issue)
  const isChunkError = error?.message?.toLowerCase().includes('chunk');

  // detect if the error might be caused by offline status
  const isOfflineError =
    !isOnline ||
    error?.message?.toLowerCase().includes('network') ||
    error?.message?.toLowerCase().includes('offline') ||
    error?.message?.toLowerCase().includes('internet') ||
    error?.message?.toLowerCase().includes('failed to fetch');

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <Background>
      <Overlay height="75%" width="100%">
        <Stack alignItems="center">
          {!isChunkError && !isOfflineError && (
            <Typography variant="h1" mt={0}>
              Sorry!
            </Typography>
          )}
          <Box textAlign="center">
            <Typography variant="h2">
              {isChunkError
                ? 'The Bento portal has been updated, please refresh your browser tab to get the latest changes.'
                : isOfflineError
                  ? 'You appear to be offline. Please check your internet connection.'
                  : `There's a problem on our end.`}
            </Typography>
          </Box>
          {!isChunkError && !isOfflineError && (
            <Typography py={1}>If the issue persists, contact Bento at smile@bento.net.</Typography>
          )}
          <Button
            color="primary"
            variant="outlined"
            sx={{
              borderRadius: 25,
              border: 'none !important',
              backgroundColor: `${pureWhite} !important`,
              mt: 2,
            }}
            onClick={(e) => {
              e.preventDefault();
              if (isChunkError) {
                window.location.reload();
              } else if (isOfflineError) {
                window.location.reload();
              } else {
                resetError();
                window.location.href = '/';
              }
            }}
          >
            {isChunkError ? 'Refresh' : isOfflineError ? 'Try Again' : 'Back to the home page'}
          </Button>
        </Stack>
      </Overlay>
    </Background>
  );
}

export default ErrorPage;
